// src/components/ProjectManagement/ProjectProgress.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { db } from '../../firebase/config';
import { collection, query, where, onSnapshot } from 'firebase/firestore';

// Global cache for project tasks
let cachedTasksMap = new Map(); // projectId -> tasks array
let lastFetchMap = new Map(); // projectId -> timestamp
let activeListeners = new Map(); // Track active listeners
const CACHE_DURATION = 30000; // 30 seconds

const ProjectProgress = ({ projectId }) => {
  const [tasks, setTasks] = useState(() => {
    // Initialize from cache if available
    const cached = cachedTasksMap.get(projectId || 'all');
    return cached || [];
  });
  const [loading, setLoading] = useState(!cachedTasksMap.has(projectId || 'all'));
  const [expandedTask, setExpandedTask] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('all');
  
  const debounceTimerRef = useRef(null);
  const isMountedRef = useRef(true);

  // OPTIMIZED: Calculate progress with memoization
  const calculateProgress = useCallback(() => {
    if (tasks.length === 0) return 0;
    const completed = tasks.filter(t => t.status === 'completed' && t.qcVerified === true).length;
    return Math.round((completed / tasks.length) * 100);
  }, [tasks]);

  // OPTIMIZED: Get status counts with single pass
  const getStatusCounts = useCallback(() => {
    let pending = 0;
    let ongoing = 0;
    let completed = 0;
    let qcPending = 0;
    let overdue = 0;
    const now = new Date();
    
    for (const task of tasks) {
      if (task.status === 'pending') {
        pending++;
        if (task.dueDate && task.dueDate.toDate() < now) {
          overdue++;
        }
      } else if (task.status === 'ongoing') {
        ongoing++;
      } else if (task.status === 'completed') {
        if (task.qcVerified) {
          completed++;
        } else {
          qcPending++;
        }
      }
    }
    
    return { pending, ongoing, completed, qcPending, overdue };
  }, [tasks]);

  // OPTIMIZED: Memoized filtered tasks
  const filteredTasks = useMemo(() => {
    const now = new Date();
    return tasks.filter(task => {
      if (selectedFilter === 'all') return true;
      if (selectedFilter === 'pending') return task.status === 'pending';
      if (selectedFilter === 'ongoing') return task.status === 'ongoing';
      if (selectedFilter === 'completed') return task.status === 'completed' && task.qcVerified === true;
      if (selectedFilter === 'qc-pending') return task.status === 'completed' && task.qcVerified === false;
      if (selectedFilter === 'overdue') return task.status === 'pending' && task.dueDate && task.dueDate.toDate() < now;
      return true;
    });
  }, [tasks, selectedFilter]);

  // Memoize counts and progress
  const counts = useMemo(() => getStatusCounts(), [getStatusCounts]);
  const progress = useMemo(() => calculateProgress(), [calculateProgress]);

  // OPTIMIZED: Helper functions with useCallback
  const getTaskStatusColor = useCallback((task) => {
    if (task.status === 'completed' && task.qcVerified) return '#10b981';
    if (task.status === 'completed' && !task.qcVerified) return '#f59e0b';
    if (task.status === 'ongoing') return '#3b82f6';
    if (task.status === 'pending') {
      if (task.dueDate && task.dueDate.toDate() < new Date()) return '#ef4444';
      return '#f59e0b';
    }
    return '#9ca3af';
  }, []);

  const getNoteBackgroundColor = useCallback((task) => {
    if (task.status === 'completed' && task.qcVerified) return '#d1fae5';
    if (task.status === 'completed' && !task.qcVerified) return '#fef3c7';
    if (task.status === 'ongoing') return '#dbeafe';
    if (task.status === 'pending') {
      if (task.dueDate && task.dueDate.toDate() < new Date()) return '#fee2e2';
      return '#fef3c7';
    }
    return '#f3f4f6';
  }, []);

  const getNoteTextColor = useCallback((task) => {
    if (task.status === 'completed' && task.qcVerified) return '#065f46';
    if (task.status === 'completed' && !task.qcVerified) return '#92400e';
    if (task.status === 'ongoing') return '#1e40af';
    if (task.status === 'pending') {
      if (task.dueDate && task.dueDate.toDate() < new Date()) return '#991b1b';
      return '#92400e';
    }
    return '#4b5563';
  }, []);

  const getStatusIcon = useCallback((task) => {
    if (task.status === 'completed' && task.qcVerified) return '✅';
    if (task.status === 'completed' && !task.qcVerified) return '⏳';
    if (task.status === 'ongoing') return '🔵';
    if (task.status === 'pending') {
      if (task.dueDate && task.dueDate.toDate() < new Date()) return '⚠️';
      return '🟠';
    }
    return '📋';
  }, []);

  const getStatusText = useCallback((task) => {
    if (task.status === 'completed' && task.qcVerified) return 'Verified';
    if (task.status === 'completed' && !task.qcVerified) return 'QC Pending';
    if (task.status === 'ongoing') return 'In Progress';
    if (task.status === 'pending') {
      if (task.dueDate && task.dueDate.toDate() < new Date()) return 'Overdue';
      return 'Pending';
    }
    return task.status;
  }, []);

  const getTaskStatusClass = useCallback((task) => {
    if (task.status === 'completed' && task.qcVerified) return 'completed-task';
    if (task.status === 'completed' && !task.qcVerified) return 'qc-task';
    if (task.status === 'ongoing') return 'ongoing-task';
    if (task.status === 'pending') {
      if (task.dueDate && task.dueDate.toDate() < new Date()) return 'overdue-task';
      return 'pending-task';
    }
    return 'pending-task';
  }, []);

  const formatDuration = useCallback((task) => {
    if (task.durationUnit === 'hours') return `${task.durationValue}h`;
    if (task.durationUnit === 'days') return `${task.durationValue}d`;
    if (task.durationUnit === 'weeks') return `${task.durationValue}w`;
    return `${task.estimatedHours}h`;
  }, []);

  // OPTIMIZED: Main data fetching with caching
  useEffect(() => {
    isMountedRef.current = true;
    const cacheKey = projectId || 'all';
    const now = Date.now();
    const cachedTasks = cachedTasksMap.get(cacheKey);
    const lastFetch = lastFetchMap.get(cacheKey) || 0;
    
    // Use cache if available and not expired
    if (cachedTasks && (now - lastFetch) < CACHE_DURATION) {
      setTasks(cachedTasks);
      setLoading(false);
    }
    
    let q;
    if (projectId) {
      q = query(collection(db, 'tasks'), where('projectId', '==', projectId));
    } else {
      q = query(collection(db, 'tasks'));
    }
    
    // Clean up previous listener for this project
    if (activeListeners.has(cacheKey)) {
      activeListeners.get(cacheKey)();
      activeListeners.delete(cacheKey);
    }
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!isMountedRef.current) return;
      
      // Debounce updates to prevent excessive re-renders
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      
      debounceTimerRef.current = setTimeout(() => {
        const tasksData = snapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data(),
          // Pre-convert dates for better performance
          dueDateObj: doc.data().dueDate?.toDate?.() || null,
          startDateObj: doc.data().startDate?.toDate?.() || null,
          endDateObj: doc.data().endDate?.toDate?.() || null,
          createdAtObj: doc.data().createdAt?.toDate?.() || null,
          completedAtObj: doc.data().completedAt?.toDate?.() || null
        }));
        
        // Update cache
        cachedTasksMap.set(cacheKey, tasksData);
        lastFetchMap.set(cacheKey, Date.now());
        
        if (isMountedRef.current) {
          setTasks(tasksData);
          setLoading(false);
        }
      }, 100); // 100ms debounce
    }, (error) => {
      console.error('Error fetching tasks:', error);
      if (isMountedRef.current) {
        setLoading(false);
      }
    });
    
    activeListeners.set(cacheKey, unsubscribe);
    
    return () => {
      isMountedRef.current = false;
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (activeListeners.has(cacheKey)) {
        activeListeners.get(cacheKey)();
        activeListeners.delete(cacheKey);
      }
    };
  }, [projectId]);

  if (loading && tasks.length === 0) return (
    <div className="progress-loading">
      <div className="loading-spinner-premium"></div>
      <p>Loading project insights...</p>
    </div>
  );

  return (
    <div className="progress-premium">
      <div className="progress-hero">
        <div className="hero-content">
          <h1>Project Analytics</h1>
          <p>Track progress, monitor tasks, and manage your workflow</p>
        </div>
        <div className="hero-stats">
          <div className="hero-stat">
            <span className="hero-stat-value">{tasks.length}</span>
            <span className="hero-stat-label">Total Tasks</span>
          </div>
          <div className="hero-stat">
            <span className="hero-stat-value">{counts.completed}</span>
            <span className="hero-stat-label">Completed</span>
          </div>
          <div className="hero-stat">
            <span className="hero-stat-value">{progress}%</span>
            <span className="hero-stat-label">Progress</span>
          </div>
        </div>
      </div>

      <div className="progress-dashboard">
        <div className="progress-ring-card">
          <div className="ring-container">
            <svg className="progress-ring" width="180" height="180" viewBox="0 0 180 180">
              <circle className="ring-bg" cx="90" cy="90" r="78" fill="none" stroke="#e5e7eb" strokeWidth="12"/>
              <circle 
                className="ring-fill" 
                cx="90" 
                cy="90" 
                r="78" 
                fill="none" 
                stroke="url(#gradient)" 
                strokeWidth="12"
                strokeDasharray={`${2 * Math.PI * 78}`}
                strokeDashoffset={`${2 * Math.PI * 78 * (1 - progress / 100)}`}
                strokeLinecap="round"
                transform="rotate(-90 90 90)"
              />
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="var(--maroon-primary)" />
                  <stop offset="100%" stopColor="var(--maroon-light)" />
                </linearGradient>
              </defs>
            </svg>
            <div className="ring-center">
              <span className="ring-percentage">{progress}%</span>
              <span className="ring-label">Complete</span>
            </div>
          </div>
          <div className="ring-stats">
            <div className="ring-stat"><div className="ring-stat-dot" style={{ background: '#ef4444' }}></div><span>{counts.overdue} Overdue</span></div>
            <div className="ring-stat"><div className="ring-stat-dot" style={{ background: '#3b82f6' }}></div><span>{counts.ongoing} In Progress</span></div>
            <div className="ring-stat"><div className="ring-stat-dot" style={{ background: '#f59e0b' }}></div><span>{counts.qcPending} QC Pending</span></div>
            <div className="ring-stat"><div className="ring-stat-dot" style={{ background: '#10b981' }}></div><span>{counts.completed} Verified</span></div>
          </div>
        </div>

        <div className="stats-premium-grid">
          <div className="stat-premium-card pending"><div className="stat-premium-icon">📋</div><div className="stat-premium-info"><span className="stat-premium-value">{counts.pending}</span><span className="stat-premium-label">Pending</span></div><div className="stat-premium-trend">awaiting action</div></div>
          <div className="stat-premium-card ongoing"><div className="stat-premium-icon">⚙️</div><div className="stat-premium-info"><span className="stat-premium-value">{counts.ongoing}</span><span className="stat-premium-label">In Progress</span></div><div className="stat-premium-trend">active now</div></div>
          <div className="stat-premium-card qc"><div className="stat-premium-icon">🎛️</div><div className="stat-premium-info"><span className="stat-premium-value">{counts.qcPending}</span><span className="stat-premium-label">QC Verification</span></div><div className="stat-premium-trend">needs review</div></div>
          <div className="stat-premium-card completed"><div className="stat-premium-icon">✅</div><div className="stat-premium-info"><span className="stat-premium-value">{counts.completed}</span><span className="stat-premium-label">Completed</span></div><div className="stat-premium-trend">done & verified</div></div>
          <div className="stat-premium-card overdue"><div className="stat-premium-icon">⚠️</div><div className="stat-premium-info"><span className="stat-premium-value">{counts.overdue}</span><span className="stat-premium-label">Overdue</span></div><div className="stat-premium-trend">requires attention</div></div>
        </div>
      </div>

      <div className="tasks-premium-section">
        <div className="tasks-premium-header">
          <div className="tasks-title"><h3>Task Inventory</h3><span className="tasks-subtitle">Detailed breakdown of all project tasks</span></div>
          <div className="tasks-filters">
            <button className={`filter-chip ${selectedFilter === 'all' ? 'active' : ''}`} onClick={() => setSelectedFilter('all')}>All</button>
            <button className={`filter-chip ${selectedFilter === 'pending' ? 'active' : ''}`} onClick={() => setSelectedFilter('pending')}>Pending</button>
            <button className={`filter-chip ${selectedFilter === 'ongoing' ? 'active' : ''}`} onClick={() => setSelectedFilter('ongoing')}>Ongoing</button>
            <button className={`filter-chip ${selectedFilter === 'qc-pending' ? 'active' : ''}`} onClick={() => setSelectedFilter('qc-pending')}>QC Pending</button>
            <button className={`filter-chip ${selectedFilter === 'completed' ? 'active' : ''}`} onClick={() => setSelectedFilter('completed')}>Completed</button>
            <button className={`filter-chip ${selectedFilter === 'overdue' ? 'active' : ''}`} onClick={() => setSelectedFilter('overdue')}>Overdue</button>
          </div>
        </div>

        <div className="tasks-premium-grid">
          {filteredTasks.map((task) => (
            <div key={task.id} className={`task-premium-card ${getTaskStatusClass(task)}`}>
              <div className="task-status-badge" style={{ background: getTaskStatusColor(task) }}>
                <span className="status-icon">{getStatusIcon(task)}</span>
                <span className="status-text">{getStatusText(task)}</span>
              </div>
              
              <div className="task-content">
                <div className="task-title-row">
                  <h4>{task.title}</h4>
                  <div className="task-badges">
                    <span className="badge-duration">{formatDuration(task)}</span>
                    {task.priority && task.priority !== 'normal' && (
                      <span className={`badge-priority ${task.priority}`}>
                        {task.priority === 'urgent' ? '🔴 Urgent' : task.priority === 'high' ? '🟠 High' : '🟢 Low'}
                      </span>
                    )}
                  </div>
                </div>
                
                {task.description && (
                  <p className="task-description">
                    {expandedTask === task.id ? task.description : task.description.substring(0, 120)}
                    {task.description.length > 120 && (
                      <button className="expand-btn" onClick={() => setExpandedTask(expandedTask === task.id ? null : task.id)}>
                        {expandedTask === task.id ? 'Show less' : '...more'}
                      </button>
                    )}
                  </p>
                )}
                
                <div className="task-meta-row">
                  {task.category && <span className="meta-item"><span>📁</span> {task.category}</span>}
                  {task.assignedToName && <span className="meta-item"><span>👤</span> {task.assignedToName}</span>}
                  {task.dueDate && (
                    <span className={`meta-item ${task.dueDate.toDate() < new Date() && task.status !== 'completed' ? 'overdue-meta' : ''}`}>
                      <span>📅</span> Due: {task.dueDate.toDate().toLocaleDateString()}
                    </span>
                  )}
                </div>
                
                {task.assignmentNote && (
                  <div className="task-note" style={{ background: getNoteBackgroundColor(task), color: getNoteTextColor(task), borderLeft: `3px solid ${getTaskStatusColor(task)}` }}>
                    <span>💬</span>
                    <span>{task.assignmentNote}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        
        {filteredTasks.length === 0 && (
          <div className="empty-premium"><div className="empty-premium-icon">📭</div><h4>No tasks found</h4><p>No tasks match the selected filter</p></div>
        )}
      </div>
    </div>
  );
};

export default ProjectProgress;